/**
 * Catalog PDF export configurator.
 *
 * Renders a dialog where the user (admin) can:
 *   • See every category that has at least 1 active product (with count).
 *   • Toggle which categories to include in the export.
 *   • Reorder categories with up/down arrows — the order in the dialog is
 *     the order the categories appear in the PDF.
 *   • Toggle the company logo in the header.
 *
 * On confirm it POSTs to `/api/catalog/export-pdf` with the ordered list,
 * receives a PDF blob and triggers the browser download.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { ArrowUp, ArrowDown, FileText, Loader2 } from "lucide-react";
import { api } from "@/App";
import { toast } from "sonner";

export default function CatalogExportDialog({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [cats, setCats] = useState([]); // [{categoria, count, selected: bool}] — order is render order
  const [includeLogo, setIncludeLogo] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/catalog/categories")
      .then(({ data }) => {
        const rows = (data?.categorias || []).map(c => ({ ...c, selected: true }));
        setCats(rows);
      })
      .catch(() => toast.error("No pude cargar las categorías"))
      .finally(() => setLoading(false));
  }, [open]);

  const move = (idx, delta) => {
    setCats(prev => {
      const next = [...prev];
      const j = idx + delta;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const toggle = (idx) => {
    setCats(prev => prev.map((c, i) => i === idx ? { ...c, selected: !c.selected } : c));
  };

  const toggleAll = (val) => {
    setCats(prev => prev.map(c => ({ ...c, selected: val })));
  };

  const doExport = async () => {
    const ordered = cats.filter(c => c.selected).map(c => c.categoria);
    if (ordered.length === 0) {
      toast.error("Tenés que elegir al menos una categoría");
      return;
    }
    setExporting(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/catalog/export-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          categorias_ordenadas: ordered,
          incluir_sin_categoria: ordered.includes("Sin categoría"),
          incluir_logo: includeLogo,
        }),
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", "catalogo.pdf");
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Catálogo PDF generado");
      onOpenChange(false);
    } catch {
      toast.error("Error al generar el PDF");
    } finally {
      setExporting(false);
    }
  };

  const selectedCount = cats.filter(c => c.selected).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-sm" data-testid="catalog-export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-rose-600" />
            Exportar catálogo PDF
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-slate-400">Cargando categorías…</div>
        ) : cats.length === 0 ? (
          <div className="py-10 text-center text-slate-400">
            Aún no etiquetaste tus productos con categorías. Editá cada producto y elegí una categoría en la sección "Categoría".
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-xs text-slate-600">
                Elegí qué categorías incluir y arrastrá con las flechitas para ordenarlas. Se exportarán <strong>{selectedCount}</strong> de {cats.length}.
              </p>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" className="h-7 rounded-sm" onClick={() => toggleAll(true)} data-testid="catalog-select-all">Todas</Button>
                <Button size="sm" variant="ghost" className="h-7 rounded-sm" onClick={() => toggleAll(false)} data-testid="catalog-select-none">Ninguna</Button>
              </div>
            </div>

            <div className="border border-slate-200 rounded-sm max-h-[40vh] overflow-y-auto divide-y divide-slate-100">
              {cats.map((c, idx) => (
                <div key={c.categoria}
                  className="flex items-center gap-2 px-2.5 py-2 hover:bg-slate-50"
                  data-testid={`catalog-cat-row-${idx}`}>
                  <Checkbox
                    checked={c.selected}
                    onCheckedChange={() => toggle(idx)}
                    className="rounded-sm"
                    data-testid={`catalog-cat-check-${idx}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{c.categoria}</p>
                    <p className="text-[11px] text-slate-500">{c.count} producto{c.count === 1 ? "" : "s"}</p>
                  </div>
                  <Button size="icon" variant="ghost" disabled={idx === 0}
                    onClick={() => move(idx, -1)}
                    className="h-7 w-7 rounded-sm"
                    aria-label="Subir"
                    data-testid={`catalog-cat-up-${idx}`}>
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" disabled={idx === cats.length - 1}
                    onClick={() => move(idx, +1)}
                    className="h-7 w-7 rounded-sm"
                    aria-label="Bajar"
                    data-testid={`catalog-cat-down-${idx}`}>
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
              <div>
                <p className="text-sm font-medium">Mostrar logo en el encabezado</p>
                <p className="text-[11px] text-slate-500">Cargá tu logo en "Mi Empresa" para que aparezca.</p>
              </div>
              <Switch checked={includeLogo} onCheckedChange={setIncludeLogo} data-testid="catalog-toggle-logo" />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={doExport}
            disabled={exporting || cats.length === 0 || selectedCount === 0}
            className="btn-primary rounded-sm"
            data-testid="catalog-export-confirm"
          >
            {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
            Generar PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
